import { Badge, Button, Card, Group, SimpleGrid, Stack, Table, Text, Title } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { api, unwrap } from '../api.ts';
import { changedFields, dayWindow, roleLabel } from '../format.ts';
import { Failure, Loading, Page, useMe } from '../ui.tsx';

function Stat(props: { label: string; value: ReactNode; to: string; hint?: string }) {
  return (
    <Card withBorder padding="lg" component={Link} to={props.to}>
      <Text size="sm" c="dimmed">
        {props.label}
      </Text>
      <Title order={2} mt={4}>
        {props.value}
      </Title>
      {props.hint ? (
        <Text size="xs" c="dimmed" mt={4}>
          {props.hint}
        </Text>
      ) : null}
    </Card>
  );
}

export function HomePage() {
  const me = useMe();
  const buildings = useQuery({ queryKey: ['buildings'], queryFn: () => unwrap(api().GET('/v1/buildings')) });
  const users = useQuery({ queryKey: ['users'], queryFn: () => unwrap(api().GET('/v1/users')) });
  const audit = useQuery({
    queryKey: ['audit'],
    queryFn: () => unwrap(api().GET('/v1/audit-log', { params: { query: { limit: 500 } } })),
  });
  const first = buildings.data?.[0];
  const today = new Date().toISOString().slice(0, 10);
  const bookings = useQuery({
    queryKey: ['bookings', first?.id, today],
    enabled: Boolean(first),
    queryFn: () =>
      unwrap(
        api().GET('/v1/booked-resources', {
          params: {
            query: { ...dayWindow(today, first?.timeZone), buildingId: first?.id ?? '', mine: false },
          },
        }),
      ),
  });
  if (!me.data) return <Loading />;
  const people = users.data ?? [];
  const active = people.filter((user) => user.status === 'active').length;
  const invited = people.filter((user) => user.status === 'invited').length;
  const live = bookings.data?.filter((booking) => booking.status !== 'cancelled').length;
  return (
    <Page
      title={`Hello, ${me.data.displayName?.split(' ')[0] ?? 'there'}`}
      description={<>Signed in as {roleLabel(me.data)}. Everything you change here is audited.</>}
    >
      <Failure error={buildings.error ?? users.error} />
      <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }}>
        <Stat label="Buildings" value={buildings.data?.length ?? '…'} to="/buildings" />
        <Stat
          label="Users"
          value={active}
          to="/users"
          hint={invited > 0 ? `${invited} invited` : undefined}
        />
        <Stat
          label="Bookings today"
          value={live ?? '…'}
          to="/bookings"
          hint={first ? `in ${first.name}` : undefined}
        />
        <Stat
          label="Changes this week"
          value={
            audit.data?.filter((entry) => Date.now() - new Date(entry.at).getTime() < 7 * 86_400_000)
              .length ?? '…'
          }
          to="/audit"
        />
      </SimpleGrid>
      <Card withBorder padding="lg">
        <Group justify="space-between" mb="sm">
          <Text fw={700}>Recent changes</Text>
          <Button
            component={Link}
            to="/audit"
            variant="subtle"
            size="xs"
            rightSection={<IconArrowRight size={14} />}
          >
            Audit log
          </Button>
        </Group>
        {audit.isPending ? <Loading /> : null}
        <Stack gap={0}>
          <Table>
            <Table.Tbody>
              {audit.data?.slice(0, 8).map((entry) => (
                <Table.Tr key={entry.id}>
                  <Table.Td w={180}>
                    <Text size="sm" c="dimmed">
                      {new Date(entry.at).toLocaleString()}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="ink" mr="xs">
                      {entry.action}
                    </Badge>
                    {entry.entity}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {changedFields(entry.before, entry.after).slice(0, 4).join(', ')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      </Card>
    </Page>
  );
}
